'use client';

import { c as _c } from "react/compiler-runtime";
import { AlertTriangle, ExternalLink } from 'lucide-react';
import type { SdkCompatibilityReason } from '@/app/editor/hooks/useInitApp';
import { sdk } from '@/app/sdk';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { PRODUCT_NAME, SDK_RELEASE_URL } from '@/const';
interface VersionCheckDialogProps {
  isOpen: boolean;
  currentVersion: string;
  requiredVersion: string;
  currentEngineVersion?: string;
  requiredEngineVersion?: string;
  reason?: SdkCompatibilityReason;
  onRetry?: () => void;
}
const reasonDescriptions: Record<SdkCompatibilityReason, string> = {
  none: 'A manual update is required before continuing.',
  runtimeVersionTooOld: 'Your installed App (SDK) version is below the required runtime version.',
  engineVersionTooOld: 'Your installed App (SDK) bundles an older engine than this editor build expects.',
  engineVersionUnknown: 'The engine version bundled with your App (SDK) could not be verified reliably.',
  belowManualFloor: 'This App (SDK) is too old to finish in-app update reliably.',
  updaterUnavailable: 'In-app update is unavailable for this App (SDK) build. Retry Check may still start recovery if your build supports it.',
  updaterFailed: 'In-app update check failed for this App (SDK) build. Use Retry Check to try again.',
  noCompatibleUpdateFound: 'No compatible in-app update was found yet. Retry Check may start an in-app download when one becomes available.',
  updaterReportedError: 'In-app updater reported an error. Use Retry Check to try recovery again, or download manually.',
  unknownVersion: 'App (SDK) version could not be verified reliably.'
};
const isEngineCompatibilityReason = (reason: SdkCompatibilityReason): boolean => reason === 'engineVersionTooOld' || reason === 'engineVersionUnknown';
export const VersionCheckDialog = t0 => {
  const $ = _c(26);
  const {
    isOpen,
    currentVersion,
    requiredVersion,
    currentEngineVersion,
    requiredEngineVersion,
    reason: t1,
    onRetry
  } = t0;
  const reason = t1 === undefined ? "none" : t1;
  let t2;
  if ($[0] !== reason) {
    t2 = isEngineCompatibilityReason(reason);
    $[0] = reason;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const isEngineFailure = t2;
  const handleDownload = _temp;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <DialogTitle className="text-primary text-2xl flex items-center gap-2"><AlertTriangle className="h-6 w-6 shrink-0" />Manual Update Required</DialogTitle>;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== currentEngineVersion || $[4] !== currentVersion || $[5] !== isEngineFailure || $[6] !== requiredEngineVersion || $[7] !== requiredVersion) {
    t4 = isEngineFailure ? <><div>Your App (SDK) engine version <strong>{currentEngineVersion || "unknown"}</strong> cannot continue with this editor build.</div><div>The required engine version is <strong>{requiredEngineVersion || "unknown"}</strong> (major.minor).</div></> : <><div>Your {PRODUCT_NAME} App (SDK) version <strong>{currentVersion}</strong> cannot continue with this editor build.</div><div>The minimum required App (SDK) version is <strong>{requiredVersion}</strong>.</div></>;
    $[3] = currentEngineVersion;
    $[4] = currentVersion;
    $[5] = isEngineFailure;
    $[6] = requiredEngineVersion;
    $[7] = requiredVersion;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const t5 = reasonDescriptions[reason];
  let t6;
  if ($[9] !== t5) {
    t6 = <div>{t5}</div>;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <div>Please download and install the latest version manually to continue using the editor.</div>;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== t4 || $[13] !== t6) {
    t8 = <DialogHeader>{t3}<DialogDescription asChild={true} className="space-y-3 text-base"><div>{t4}{t6}{t7}</div></DialogDescription></DialogHeader>;
    $[12] = t4;
    $[13] = t6;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== onRetry) {
    t9 = onRetry && <Button variant="outline" onClick={onRetry}>Retry Check</Button>;
    $[15] = onRetry;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <Button onClick={handleDownload} className="gap-2"><ExternalLink className="h-4 w-4" />Download Latest App (SDK)</Button>;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== t9) {
    t11 = <div className="flex justify-center gap-2 pt-4">{t9}{t10}</div>;
    $[18] = t9;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== t11 || $[21] !== t8) {
    t12 = <DialogContent className="sm:max-w-lg gap-4 p-6" showCloseButton={false} onEscapeKeyDown={_temp2} onPointerDownOutside={_temp3} onInteractOutside={_temp4}>{t8}{t11}</DialogContent>;
    $[20] = t11;
    $[21] = t8;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== isOpen || $[24] !== t12) {
    t13 = <Dialog open={isOpen} modal={true}>{t12}</Dialog>;
    $[23] = isOpen;
    $[24] = t12;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  return t13;
};
VersionCheckDialog.displayName = 'VersionCheckDialog';
async function _temp() {
  if (sdk?.os.openUrl) {
    await sdk.os.openUrl(SDK_RELEASE_URL);
    if (sdk?.window.close) {
      sdk.window.close();
    }
  } else {
    window.open(SDK_RELEASE_URL, "_blank");
  }
}
function _temp2(e) {
  return e.preventDefault();
}
function _temp3(e_0) {
  return e_0.preventDefault();
}
function _temp4(e_1) {
  return e_1.preventDefault();
}